import { faUsers, faWrench } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router';
import LoadingScreen from '../../components/muiLegacy/LoadingScreen/LoadingScreen';
import VoxelPageContainer from '../../components/voxel/Containers/VoxelPageContainer';
import VoxelMenu, { MenuItem } from '../../components/voxel/Menu/VoxelMenu';
import VoxelText from '../../components/voxel/Text/VoxelText';
import VoxelAvatar from '../../components/voxel/VoxelAvatar';
import { AnalysisColorPaletteGq } from '../../graphql/projects/models/AnalysisColorPaletteGq';
import { CurrencyOptionGq } from '../../graphql/projects/models/CurrencyOptionGq';
import { GoogleAdsLanguageGq } from '../../graphql/projects/models/GoogleAdsLanguageGq';
import { GoogleAdsLocationGq } from '../../graphql/projects/models/GoogleAdsLocationGq';
import { usersApiService } from '../../graphql/users/usersApiService';
import useIsScrolling from '../../logic/otherUtilities/useIsScrolling';
import StartPageAppBar from '../StartPage/components/StartPageAppBar';
import DataSettingsPage from './DataSettingsPage/DataSettingsPage';
import NotificationsPage from './NotificationsPage';
import { useCancelDialog } from '../../components/muiLegacy/Dialogs/CancelDialog/useCancelDialog';
import UnsavedChangesDialog from '../../components/muiLegacy/Dialogs/CancelDialog/UnsavedChangesDialog';

export interface UserPageProps { }

export enum UserMenuChoices {
  DataSettings = 'dataSettings',
  Notifications = 'notifications',
}
export type ReloadFunction = () => Promise<unknown>;

export interface Paging {
  page: number;
  amountPerPage: number;
}

export interface UserSettingsFormState {
  palette?: AnalysisColorPaletteGq;
  language?: GoogleAdsLanguageGq;
  country?: GoogleAdsLocationGq;
  city?: GoogleAdsLocationGq;
  currency: CurrencyOptionGq;
}

export const UserPage: React.FC<UserPageProps> = ({ }) => {
  // Get user
  const {
    user,
    team,
    query: userQuery,
    availableUsers,
  } = usersApiService.queries.useGetCurrentUser();

  const [reloadFunctions, setReloadFunctions] = useState<ReloadFunction[]>([]);
  const handleReload = useCallback(
    () => Promise.all(reloadFunctions.map((fn) => fn())),
    [reloadFunctions]
  );

  useEffect(() => {
    setReloadFunctions((prev) => [...prev, userQuery.refetch]);
  }, [userQuery.refetch]);

  const isScrolling = useIsScrolling();



  const menuItems: MenuItem[] = [
    {
      name: 'Data settings',
      id: UserMenuChoices.DataSettings,
      icon: <FontAwesomeIcon icon={faWrench} />,
      disabled: false,
    },
    {
      name: 'Email notifications',
      id: UserMenuChoices.Notifications,
      icon: <FontAwesomeIcon icon={faUsers} />,
      disabled: false,
    },
  ];

  // Combined cancel dialog bag
  const [combo, setCombo] = useState<{
    currencyDirty?: boolean;
    currencySaveFn?: () => Promise<unknown>;
    paletteDirty?: boolean;
    paletteSaveFn?: () => Promise<unknown>;
    languageDirty?: boolean;
    languageSaveFn?: () => Promise<unknown>;
    locationDirty?: boolean;
    locationSaveFn?: () => Promise<unknown>;
  }>({});

  const dirty =
    !!combo.currencyDirty ||
    !!combo.paletteDirty ||
    !!combo.languageDirty ||
    !!combo.locationDirty;

  const combinedSave = useCallback(() => {
    return Promise.resolve()
      .then(() =>
        combo.currencyDirty && combo.currencySaveFn
          ? combo.currencySaveFn()
          : Promise.resolve()
      )
      .then(() =>
        combo.paletteDirty && combo.paletteSaveFn
          ? combo.paletteSaveFn()
          : Promise.resolve()
      )
      .then(() =>
        combo.languageDirty && combo.languageSaveFn ? combo.languageSaveFn() : Promise.resolve()
      )
      .then(() =>
        combo.locationDirty && combo.locationSaveFn ? combo.locationSaveFn() : Promise.resolve()
      );
  }, [combo]);

  const cancelDialogBag = useCancelDialog(dirty, combinedSave);

  // Navigation
  const match = useRouteMatch();
  const history = useHistory();
  const path = '/user';
  const selectedId = (match.params as any).tab;
  const handleSelectMenuItem = (itemId: string) => history.push(`${path}/${itemId}`);
  const wrappedHandleSelectMenuItem = cancelDialogBag.wrapAction(handleSelectMenuItem);

  // Rendering (no hooks after this)
  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <>
      <UnsavedChangesDialog cancelDialogBag={cancelDialogBag} />
      <StartPageAppBar
        team={team}
        user={user}
        availableUsers={availableUsers}
        isScrolling={isScrolling}
        loading={userQuery?.loading}
      />
      <VoxelPageContainer>
        {/* Top section */}
        <div className={'flex flex-row gap-8 items-center flex-nowrap py-4'}>
          {/* User avatar */}
          {!user.picture?.length ? (
            <VoxelAvatar name={user.email} size={'lg'} />
          ) : (
            <img src={user.picture} className={'rounded-full h-24 w-auto'} />
          )}
          {/* User information */}
          <div className="flex flex-col gap-1">
            {!!user.givenName?.length && !!user.familyName?.length && (
              <VoxelText variant={'title2'}>
                {user.givenName} {user.familyName}
              </VoxelText>
            )}
            <VoxelText variant={'bread-normal'}>{user.email}</VoxelText>
          </div>
        </div>
        {/* Bottom section */}
        <div className="mt-12 grid grid-cols-12 gap-x-12">
          <div className='col-span-4 md:col-span-3 2xl:col-span-2'>
            {/* Bottom section - menu */}
            <VoxelMenu
              className="w-full"
              menuListItems={menuItems}
              selectedId={selectedId}
              setSelectedId={wrappedHandleSelectMenuItem}
              disableArrow
            />
          </div>
          {/* Bottom section - content */}
          <div className="flex-grow col-span-8 md:col-span-9 2xl:col-span-10">
            <Switch>
              <Route
                path={path + '/' + UserMenuChoices.DataSettings}
                render={() =>
                  <DataSettingsPage
                    user={user}
                    reload={handleReload}
                    setCurrencyDirty={(dirty) =>
                      setCombo((prev) => ({ ...prev, currencyDirty: dirty }))
                    }
                    setCurrencySave={(saveFn) =>
                      setCombo((prev) => ({ ...prev, currencySaveFn: saveFn }))
                    }
                    setPaletteDirty={(dirty) =>
                      setCombo((prev) => ({ ...prev, paletteDirty: dirty }))
                    }
                    setPaletteSave={(saveFn) =>
                      setCombo((prev) => ({ ...prev, paletteSaveFn: saveFn }))
                    }
                    setLocationDirty={(dirty) =>
                      setCombo((prev) => ({ ...prev, locationDirty: dirty }))
                    }
                    setLocationSave={(saveFn) =>
                      setCombo((prev) => ({ ...prev, locationSaveFn: saveFn }))
                    }
                    setLanguageDirty={(dirty) =>
                      setCombo((prev) => ({ ...prev, languageDirty: dirty }))
                    }
                    setLanguageSave={(saveFn) =>
                      setCombo((prev) => ({ ...prev, languageSaveFn: saveFn }))
                    }
                  />
                }
              />
              <Route
                path={path + '/' + UserMenuChoices.Notifications}
                render={() => <NotificationsPage user={user} reload={handleReload} />}
              />
            </Switch>
          </div>
        </div>
      </VoxelPageContainer>
    </>
  );
};

export default UserPage;
